import { CheckOutlined, LeftOutlined, ReloadOutlined } from '@ant-design/icons'
import { Stack } from '@sureco/design-system'
import { Button } from '@surecompanies/core_components'
import { Alert } from 'antd'
import styled from 'styled-components'
import { useRouterContext } from 'components/RouterContext'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import {
  BENEFIT_INTEGRATIONS,
  ideonConfigPath,
} from 'enrollment-platform/pages/BenefitIntegrations/paths'
import Page from 'layouts/Page'
import React, { useMemo } from 'react'
import { useIdeonAgent } from './hooks/useIdeonAgent'
import CarrierIdentifiersCard from './components/CarrierIdentifiersCard'
import ProfileCard from './components/ProfileCard'
import StateLicensesCard from './components/StateLicensesCard'
import { formatIdeonAgentName } from './formatIdeonAgentName'

const benefitIntegrationsListPath = () => `/${BENEFIT_INTEGRATIONS}`

// AntD's Alert with a description aligns all children to flex-start, which
// leaves the Retry action at the top. Center only the action so the icon and
// title/description keep their natural top alignment.
const CenteredActionAlert = styled.div`
  .ant-alert-action {
    align-self: center;
  }
`

export default function IdeonAgentDetail() {
  const { router, params } = useRouterContext()
  const ideonAgentId = (params?.ideonAgentId as string | undefined) ?? null

  const {
    agent,
    loading,
    loadError,
    savingAgent,
    refreshAgent,
    saveAgentProfile,
    addStateLicense,
    editStateLicense,
    removeStateLicense,
    addCarrierIdentifier,
    editCarrierIdentifier,
    removeCarrierIdentifier,
  } = useIdeonAgent(ideonAgentId)

  const breadcrumbItems = useMemo(() => {
    const agentLabel = agent ? formatIdeonAgentName(agent) || 'Agent' : 'Agent'
    return [
      { title: 'Home', path: '/' },
      {
        title: 'Benefit Integrations',
        path: benefitIntegrationsListPath(),
      },
      // "Ideon" and "Configs" have no dedicated landing page yet — link them
      // to the Agents page so the breadcrumb chain stays traversable.
      { title: 'Ideon', path: ideonConfigPath() },
      { title: 'Configs', path: ideonConfigPath() },
      { title: 'Agents', path: ideonConfigPath() },
      { title: agentLabel },
    ]
  }, [agent])

  const pageTitle = agent
    ? formatIdeonAgentName(agent) || 'Ideon Agent'
    : 'Ideon Agent'

  return (
    <Page
      title={pageTitle}
      maxWidth="100%"
      breadcrumb={<BreadcrumbNav items={breadcrumbItems} />}
    >
      <Stack direction="col" space={24}>
        <Stack
          direction="row"
          space={12}
          style={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Button
            type="link"
            onClick={() => router.push(ideonConfigPath())}
            icon={<LeftOutlined aria-hidden="true" />}
            style={{ paddingLeft: 0 }}
          >
            Back to agents
          </Button>
          {agent?.managed ? (
            <span>
              <strong>Managed:</strong>{' '}
              <CheckOutlined
                role="img"
                aria-label="Yes"
                style={{ color: '#52c41a' }}
              />
            </span>
          ) : null}
        </Stack>

        {!loading && loadError ? (
          <CenteredActionAlert>
            <Alert
              type="error"
              showIcon
              // role="alert" so screen readers announce the failure when the
              // alert appears — AntD's Alert doesn't add it by default.
              role="alert"
              message="Failed to load agent"
              description={loadError}
              action={
                <Button
                  icon={<ReloadOutlined aria-hidden="true" />}
                  onClick={() => void refreshAgent()}
                >
                  Retry
                </Button>
              }
            />
          </CenteredActionAlert>
        ) : null}

        <ProfileCard
          agent={agent}
          loading={loading}
          savingAgent={savingAgent}
          saveAgentProfile={saveAgentProfile}
        />

        <StateLicensesCard
          loading={loading}
          stateLicenses={agent?.stateLicenses ?? []}
          addStateLicense={addStateLicense}
          editStateLicense={editStateLicense}
          removeStateLicense={removeStateLicense}
        />

        <CarrierIdentifiersCard
          loading={loading}
          carrierIdentifiers={agent?.carrierIdentifiers ?? []}
          addCarrierIdentifier={addCarrierIdentifier}
          editCarrierIdentifier={editCarrierIdentifier}
          removeCarrierIdentifier={removeCarrierIdentifier}
        />
      </Stack>
    </Page>
  )
}
